import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  Grid,
  Container,
  CircularProgress,
  Paper,
  Avatar
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Phone as PhoneCallIcon,
  CallMade as PhoneOutgoingIcon,
  CallReceived as PhoneIncomingIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Timeline as ActivityIcon,
  FlashOn as FlashOnIcon,
  Add as AddIcon,
  BarChart as BarChartIcon
} from '@mui/icons-material';
import { dashboardService } from '../services/api';
import { Link } from 'react-router-dom';

const DashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalCalls: 0,
    outboundCalls: 0,
    inboundCalls: 0,
    averageDuration: 0,
    activeAgents: 0,
    dailyStats: []
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const data = await dashboardService.getDashboardStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, description, trend, color = "primary" }) => (
    <Card 
      elevation={2}
      sx={{ 
        height: '100%',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          elevation: 4,
          transform: 'translateY(-2px)'
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box flex={1}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" component="div" fontWeight="bold" gutterBottom>
              {value}
            </Typography>
            {description && (
              <Typography variant="body2" color="text.secondary">
                {description}
              </Typography>
            )}
            {trend && (
              <Box display="flex" alignItems="center" mt={1}>
                <TrendingUpIcon 
                  sx={{ 
                    fontSize: 16, 
                    color: trend > 0 ? 'success.main' : 'error.main',
                    mr: 0.5
                  }} 
                />
                <Typography 
                  variant="body2" 
                  color={trend > 0 ? 'success.main' : 'error.main'}
                >
                  {trend > 0 ? '+' : ''}{trend}% from last week
                </Typography>
              </Box>
            )}
          </Box>
          <Avatar 
            sx={{ 
              bgcolor: `${color}.light`, 
              color: `${color}.main`,
              width: 56,
              height: 56
            }}
          >
            <Icon />
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  const QuickActionCard = ({ title, description, icon: Icon, href, color = "primary" }) => (
    <Link to={href} style={{ textDecoration: 'none' }}>
      <Card 
        elevation={2}
        sx={{ 
          height: '100%',
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            elevation: 4,
            transform: 'translateY(-2px)'
          }
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" spacing={2}>
            <Avatar 
              sx={{ 
                bgcolor: `${color}.light`, 
                color: `${color}.main`,
                width: 48,
                height: 48,
                mr: 2
              }}
            >
              <Icon />
            </Avatar>
            <Box flex={1}>
              <Typography variant="h6" component="h3" gutterBottom>
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {description}
              </Typography>
            </Box>
            <AddIcon color="action" />
          </Box>
        </CardContent>
      </Card>
    </Link>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Grid container spacing={3}>
        {/* Welcome Section */}
        <Grid item xs={12}>
          <Paper 
            elevation={2}
            sx={{ 
              p: 4,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white'
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
                  Welcome to Call Ninja
                </Typography>
                <Typography variant="h6" gutterBottom sx={{ opacity: 0.9 }}>
                  Your AI voice agents are ready to handle calls with ninja-like precision and efficiency.
                </Typography>
                <Box display="flex" alignItems="center" gap={2} mt={2}>
                  <Chip 
                    icon={<ActivityIcon />}
                    label="System Online"
                    sx={{ 
                      bgcolor: 'success.light',
                      color: 'success.contrastText'
                    }}
                  />
                  <Chip 
                    label={`${stats.activeAgents} Active Agents`}
                    variant="outlined"
                    sx={{ color: 'white', borderColor: 'white' }}
                  />
                </Box>
              </Box>
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <Avatar 
                  sx={{ 
                    width: 96, 
                    height: 96, 
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white'
                  }}
                >
                  <FlashOnIcon sx={{ fontSize: 48 }} />
                </Avatar>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Stats Grid */}
        <Grid item xs={12}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Calls"
                value={stats.totalCalls}
                icon={PhoneIcon}
                description="All time calls"
                trend={12}
                color="blue"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Outbound Calls"
                value={stats.outboundCalls}
                icon={PhoneOutgoingIcon}
                description="Initiated calls"
                trend={8}
                color="green"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Inbound Calls"
                value={stats.inboundCalls}
                icon={PhoneIncomingIcon}
                description="Received calls"
                trend={15}
                color="purple"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Active Agents"
                value={stats.activeAgents}
                icon={PeopleIcon}
                description="Ready to serve"
                trend={5}
                color="orange"
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12}>
          <Typography variant="h4" component="h2" gutterBottom fontWeight="bold">
            Quick Actions
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={4}>
              <QuickActionCard
                title="Create AI Agent"
                description="Set up a new voice agent with custom personality and skills"
                icon={PeopleIcon}
                href="/agents/create"
                color="blue"
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <QuickActionCard
                title="Make a Call"
                description="Start a call with your AI agents right now"
                icon={PhoneCallIcon}
                href="/calls/create"
                color="green"
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <QuickActionCard
                title="View Analytics"
                description="Check detailed call analytics and performance metrics"
                icon={BarChartIcon}
                href="/analytics"
                color="purple"
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12}>
          <Typography variant="h4" component="h2" gutterBottom fontWeight="bold">
            Recent Activity
          </Typography>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="body1" color="text.secondary">
                No recent activity to display
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default DashboardPage;